import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./Inquiry.css";
import { ConfigContext } from "../Context/ConfigContext";
import { useParams } from "react-router-dom";

function Inquiry() {
  const { slug } = useParams();

  const { phone_no, email, ApiUrl, apiHeaderJson } = useContext(ConfigContext);

  const productIdFromSlug = slug && slug.length > 1
    ? slug[slug.length - 1]   // take the last part as ID
    : null;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    product: "",       // stores product_id
    product_name: "",  // stores product_title
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Product options state
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  // General input change handler
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Specific handler for product select (sets both id and name)
  const handleProductChange = (e) => {
    const selectedValue = e.target.value;
    const selectedText = e.target.options[e.target.selectedIndex]?.text || "";
    setFormData({
      ...formData,
      product: selectedValue,
      product_name: selectedValue ? selectedText : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build the payload
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      product_id: formData.product || null,      // send null if not selected
      product_name: formData.product_name || "", // optional
      message: formData.message,
    };

    try {
      setSubmitLoading(true); // you may add this state
      const response = await axios.post(`${ApiUrl}apis/SendInquiryMail`, payload, {
        headers: apiHeaderJson,
      });
      const { success, message } = response.data;
      if (success) {
        setSubmitted(true);
        // Optionally reset form or show success
      } else {
        Swal.fire('Error!', message || 'Submission failed', 'error');
      }
    } catch (error) {
      console.error('Inquiry submission error:', error);
      Swal.fire('Error!', 'Something went wrong. Please try again.', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const response = await axios.get(`${ApiUrl}apis/GetProducts`, {
          headers: apiHeaderJson,
        });
        const { success, data } = response.data;
        if (success && Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts([]);
          setProductsError("No products found");
        }
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setProductsError(err.message || "Failed to load products");
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [ApiUrl, apiHeaderJson]);


  useEffect(() => {
    // Only run if we have a product ID from slug, products are loaded, and no product is selected yet
    if (productIdFromSlug && products.length > 0 && !formData.product) {
      const matchedProduct = products.find(
        (p) => String(p.product_id) === String(productIdFromSlug) // compare as strings
      );
      if (matchedProduct) {
        setFormData((prev) => ({
          ...prev,
          product: matchedProduct.product_id,
          product_name: matchedProduct.product_title,
        }));
      }
    }
  }, [products, productIdFromSlug, formData.product]);


  return (
    <div className="inquiry-page">
      {/* Hero */}
      <section className="inquiry-hero">
        <div className="inquiry-hero-content" data-aos="fade-up">
          <span className="inquiry-hero-tag">GET IN TOUCH</span>
          <h1>Send Us An <span>Inquiry</span></h1>
          <p>Interested in our products? Fill out the form and our team will get back to you.</p>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="inquiry-section">
        <div className="inquiry-container">
          {/* Form */}
          <div className="inquiry-form-card" data-aos="fade-right">
            {submitted ? (
              <div className="inquiry-success">
                <i className="ri-checkbox-circle-fill"></i>
                <h2>Inquiry Submitted!</h2>
                <p>Thank you, {formData.name || "friend"}! Our team will contact you within 24 hours.</p>
                <button className="inquiry-another-btn" onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", product: "", message: "" }); }}>
                  Send Another Inquiry
                </button>
              </div>
            ) : (
              <form className="inquiry-form" onSubmit={handleSubmit}>
                <h2>Product Inquiry</h2>
                <p className="inquiry-form-subtitle">Tell us what you're looking for</p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="product">Product of Interest</label>
                    <select
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleProductChange}
                      disabled={productsLoading}
                    >
                      <option value="">Select a product</option>
                      {productsLoading ? (
                        <option value="" disabled>Loading products...</option>
                      ) : productsError ? (
                        <option value="" disabled>Error loading products</option>
                      ) : (
                        products.map((product) => (
                          <option key={product.product_id} value={product.product_id}>
                            {product.product_title}
                          </option>
                        ))
                      )}
                    </select>
                    {productsError && (
                      <small className="text-danger">{productsError}</small>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    placeholder="Tell us more about your requirements..."
                    value={formData.message}
                    onChange={handleChange}
                    required={true}
                  ></textarea>
                </div>

                <button type="submit" disabled={submitLoading} className="inquiry-submit-btn">
                  <i className="ri-send-plane-fill"></i> {submitLoading ? "Submitting..." : "Submit Inquiry"}
                </button>
              </form>
            )}
          </div>

          {/* Side Info */}
          <div className="inquiry-side-info" data-aos="fade-left">
            <div className="inquiry-info-card">
              <h3>Contact Information</h3>

              <div className="inquiry-info-item">
                <div className="inquiry-info-icon"><i className="ri-phone-line"></i></div>
                <div>
                  <span>Phone No</span>
                  <strong>
                    <a href={`tel:${phone_no}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-dark">
                      {phone_no}
                    </a>
                  </strong>
                </div>
              </div>

              <div className="inquiry-info-item">
                <div className="inquiry-info-icon"><i className="ri-mail-line"></i></div>
                <div>
                  <span>Email</span>
                  <strong>
                    <a href={`mailto:${email}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-dark">
                      {email}
                    </a>
                  </strong>
                </div>
              </div>


            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Inquiry;